import React, { useState } from 'react';
import ServiceLayout from '../components/ServiceLayout.jsx';
import { Quote, Plus, X, Phone, Calendar, ArrowRight, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const Accordion = ({ items }) => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
      {items.map((item, index) => (
        <div key={item.title} className="border-b border-[#E8DDCE]">
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="w-full py-6 flex justify-between items-center text-left group cursor-pointer"
          >
            <h3 className="text-xl md:text-2xl font-serif text-[#3B352F] group-hover:text-[#123F13] transition-colors">
              {item.title}
            </h3>
            <div className="text-[#123F13] transition-transform duration-300">
              {openIndex === index ? <X size={24} /> : <Plus size={24} />}
            </div>
          </button>
          <AnimatePresence>
            {openIndex === index && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="pb-8 pr-4">
                  <p className="text-[#3B352F]/70 leading-relaxed text-base md:text-lg">
                    {item.content}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

const FAQAccordion = ({ items }) => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="space-y-4">
      {items.map((item, index) => (
        <div key={index} className="bg-white rounded-xl border border-[#E8DDCE] overflow-hidden shadow-sm">
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="w-full p-6 flex justify-between items-center text-left group cursor-pointer"
          >
            <h3 className="text-lg font-bold text-[#123F13]">
              {item.question}
            </h3>
            <div className="text-[#8C2F12] transition-transform duration-300">
              {openIndex === index ? <X size={20} /> : <Plus size={20} />}
            </div>
          </button>
          <AnimatePresence>
            {openIndex === index && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <div className="px-6 pb-6 text-[#3B352F]/70 leading-relaxed">
                  {item.answer}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

const Tankemylder = () => {
  const canonicalUrl = "https://corehypnose.dk/tankemylder";
  
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Behandling af tankemylder med Metakognitiv Terapi og Hypnose",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Core Hypnose v/ Lars Nissen Corell – Psykoterapeut MPF",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Valby Tingsted 7",
        "addressLocality": "Valby",
        "postalCode": "2500",
        "addressCountry": "DK"
      },
      "telephone": "+4527204671"
    },
    "description": "Få ro i hovedet og slip for tankemylder hos Lars Nissen Corell – Psykoterapeut MPF i Valby. Effektive teknikker fra metakognitiv terapi og hypnose.",
    "areaServed": "Danmark",
    "serviceType": "Psykoterapi"
  };

  return (
    <ServiceLayout 
      title="Slip for tankemylder – få ro i hovedet og overskuddet tilbage"
      subtitle="Metakognitiv terapi og hypnose er de mest effektive værktøjer til at stoppe den mentale motorvej og genvinde kontrollen over dine tanker."
      image="https://assets.auxodev.dk/assets/c5965315-775d-45c8-ad9e-70e7ef29846e/8b553c46-ea63-434e-a02c-d1c93b2cfbed.png"
      description="Føles dit hoved som en motor, der kører i tomgang med alt for mange omdrejninger? Tankemylder kan være udmattende og dræne dig for energi. Hos CoreHypnose hjælper jeg dig med at finde 'stop-knappen'."
      actions={
        <>
          <Link to="/bestil-tid" className="inline-flex items-center gap-2 bg-[#193f10] text-white px-4 py-3 rounded-md font-bold text-sm sm:text-base hover:bg-[#193f10]/90 transition-all shadow-md shrink-0">
            <Calendar size={18} /> Book første behandlingssession
          </Link>
          <a href="tel:27204671" className="inline-flex items-center gap-2 border-2 border-[#193f10] text-[#193f10] px-4 py-3 rounded-md font-bold text-sm sm:text-base hover:bg-[#faf7f2] transition-all shrink-0">
            <Phone size={18} /> Tlf. 27 20 46 71
          </a>
        </>
      }
    >
      <Helmet>
        <title>Behandling af Tankemylder i Valby | Få ro i hovedet hos CoreHypnose</title>
        <meta name="description" content="Kæmper du med tankemylder? Få effektive redskaber til at få ro i hovedet hos Lars Nissen Corell – Psykoterapeut MPF i Valby. Metakognitiv terapi og hypnose der virker." />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="Slip for tankemylder | CoreHypnose Valby" />
        <meta property="og:description" content="Genvind kontrollen over dine tanker og få ro i sindet med metakognitiv terapi og hypnose." />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      </Helmet>

      {/* Testimonial Section */}
      <div className="bg-[#123F13] text-white p-8 md:p-12 rounded-2xl relative overflow-hidden mb-16">
        <Quote className="absolute top-8 right-8 text-white/10" size={120} />
        <div className="relative z-10 max-w-2xl">
          <h3 className="text-2xl font-serif mb-6 italic">“Hvad der før fyldte det meste af min dag, fylder i dag ingenting”</h3>
          <p className="text-white/80 mb-6 leading-relaxed">
            Lars har lært mig at holde afstand til grublerier og bekymringer. Jeg kan næsten ikke få armene ned over det gode resultat. Det er en kæmpe lettelse endelig at have fået ro i hovedet.
          </p>
          <p className="font-bold">— Louise</p>
          <p className="text-white/60 text-sm mt-4 italic">Louise fik ro i tankerne – og det kan du også.</p>
        </div>
      </div>

      {/* Hvad er tankemylder sektion */}
      <section className="mb-16">
        <div className="w-full">
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-6">Hvad er tankemylder – og hvorfor opstår det?</h2>
          <div className="space-y-4 text-[#3B352F]/70 leading-relaxed text-lg">
            <p>
              Tankemylder er ikke en sygdom i sig selv, men en tilstand hvor hjernen bliver overbelastet af konstante bekymringer, grublerier eller analyser af fortid og fremtid. Det føles ofte som om, tankerne lever deres eget liv, og at man ikke kan stoppe dem, selvom man gerne vil.
            </p>
            <p>
              Mange oplever, at tankemylderet tager til, når de skal sove, eller når de har brug for at slappe af. Det skaber en indre uro, som kan føre til stress, angst og søvnløshed. Problemet er sjældent selve tankerne, men den tid og energi, man bruger på at forholde sig til dem.
            </p>
            <p>
              I metakognitiv terapi ser vi på tankemylder som en vane – en mental strategi, man har udviklet for at forsøge at løse problemer. Men i virkeligheden bliver problemerne ofte større, jo mere man tænker over dem.
            </p>
            <p className="text-base border-l-2 border-[#8C2F12]/30 pl-4 py-1">
              Nogle oplever også, at tankemylderet handler om parforholdet, kommunikationsproblemer eller usikkerhed i relationer. Her kan <Link to="/parterapi" className="text-[#8C2F12] font-semibold hover:underline">parterapi eller relationscoaching</Link> hjælpe med at bryde de fastlåste mønstre og skabe ro.
            </p>
            <p className="text-base border-l-2 border-[#8C2F12]/30 pl-4 py-1">
              For autistiske personer kan tankemylder ofte være tæt forbundet med sanseindtryk, uforudsigelighed eller forsøg på at afkode sociale situationer (maskering). Læs mere om mit <Link to="/autismevenlig-terapi" className="text-[#8C2F12] font-semibold hover:underline">autismevenlige forløb</Link>.
            </p>
            <p className="font-medium text-[#123F13]">
              Gennem behandlingen lærer du at lade tankerne være, så de kan passere af sig selv, præcis som skyer på himlen eller trafik på en vej.
            </p>
          </div>

          {/* Overblik over symptomer og typer */}
          <div className="py-16 border-t border-[#E8DDCE] mt-16">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <span className="text-[#8C2F12] text-xs font-semibold tracking-[0.15em] uppercase mb-4 block">Symptomer</span>
              <h2 className="text-4xl md:text-5xl font-serif text-[#123F13] mb-6">Kender du disse tegn?</h2>
              <p className="text-[#3B352F]/70 text-lg">
                Tankemylder kan vise sig på mange måder. Her er nogle af de mest almindelige tegn på, at dine tanker har taget overhånd.
              </p>
            </div>

            <div className="max-w-6xl mx-auto">
              <Accordion items={[
                {
                  title: "Søvnbesvær",
                  content: "Du ligger vågen i timevis, fordi tankerne kører i ring. Selvom du er træt, kan din hjerne ikke finde ro, og du ender med at analysere morgendagens opgaver eller gårsdagens hændelser."
                },
                {
                  title: "Konstant bekymring",
                  content: "Du tænker hele tiden 'hvad nu hvis...'. Du forbereder dig på alle tænkelige scenarier, ofte de negative, i et forsøg på at føle dig tryg, men resultatet er blot mere uro."
                },
                {
                  title: "Mental træthed",
                  content: "Du føler dig drænet for energi, selvom du ikke har lavet noget fysisk hårdt. Det konstante tankearbejde svarer til at køre en maraton i dit hoved hver eneste dag."
                },
                {
                  title: "Besvær med at fokusere",
                  content: "Det er svært at koncentrere sig om en bog, en film eller en samtale, fordi dine egne tanker hele tiden afbryder og trækker din opmærksomhed væk fra nuet."
                },
                {
                  title: "Fysisk uro",
                  content: "Tankemylder sætter sig ofte i kroppen som hjertebanken, spændinger i nakke og skuldre eller en knude i maven. Kroppen reagerer på det mentale pres."
                },
                {
                  title: "Beslutningslammelse",
                  content: "Selv små valg bliver uoverskuelige, fordi du overanalyserer alle muligheder og konsekvenser. Du bliver bange for at vælge forkert og ender med slet ikke at vælge."
                }
              ]} />
            </div>
          </div>

          {/* Hvorfor min behandling virker */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 py-16 border-t border-[#E8DDCE]">
            <div>
              <h2 className="text-3xl font-serif text-[#123F13] mb-6">Effektive redskaber til ro</h2>
              <ul className="space-y-3">
                {[
                  "Lær at lade tankerne passe sig selv",
                  "Genvind kontrollen over din opmærksomhed",
                  "Reducer tiden du bruger på bekymringer",
                  "Få konkrete teknikker til at stoppe grublerier",
                  "Skab ro i nervesystemet med hypnose",
                  "Bryd vanen med at overanalysere alt",
                  "Få bedre søvn og mere energi i hverdagen",
                  "Lær at skelne mellem vigtige tanker og 'støj'"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-[#3B352F]/70">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#8C2F12]" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-8 p-6 bg-[#FAF7F2] rounded-xl border border-[#E8DDCE]">
                <p className="text-[#123F13] font-medium mb-4">Vil du vide mere om hvordan metakognitiv terapi virker?</p>
                <Link to="/metakognitiv-terapi" className="text-[#8C2F12] font-bold hover:underline inline-flex items-center gap-2">
                  Læs om metakognitiv terapi <ArrowRight size={16} />
                </Link>
              </div>
            </div>
            <div className="bg-white p-8 rounded-xl border border-[#E8DDCE] shadow-sm">
              <h3 className="text-xl font-bold text-[#123F13] mb-4">Hvorfor kombinationen virker</h3>
              <p className="text-[#3B352F]/70 mb-6">
                Metakognitiv terapi giver dig de logiske og praktiske værktøjer til at forstå og ændre dine tankemønstre. Du lærer, at du faktisk har et valg, når en tanke dukker op.
              </p>
              <p className="text-[#3B352F]/70 mb-6">
                Hypnose understøtter denne proces ved at skabe en dyb tilstand af ro, hvor dit nervesystem kan slappe af. Det gør det lettere at integrere de nye teknikker og mærke, hvordan det føles, når der endelig er stille i hovedet.
              </p>
              <p className="text-[#3B352F]/70 italic">
                Mange klienter beskriver det som om, der bliver 'ryddet op' på øverste etage, så der igen bliver plads til glæde og nærvær.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="space-y-24">
        {/* Hvorfor bliver tankerne ved sektion */}
        <section className="bg-[#FAF7F2] p-8 md:p-12 rounded-2xl border border-[#E8DDCE]">
          <div className="max-w-3xl">
            <h2 className="text-3xl font-serif text-[#123F13] mb-6">Hvorfor stopper tankerne ikke bare?</h2>
            <div className="space-y-4 text-[#3B352F]/70 leading-relaxed text-lg">
              <p>
                De fleste forsøger at stoppe tankemylder ved at tænke endnu mere eller ved at prøve at 'løse' tankerne. Men det svarer til at prøve at slukke en brand med benzin. Jo mere opmærksomhed du giver tankerne, jo mere fylder de.
              </p>
              <p>
                Behandlingen hos CoreHypnose handler om at lære en helt ny måde at forholde sig til sine tanker på. Jeg arbejder ikke med indholdet af dine tanker (hvad du tænker), men med din proces (hvordan du tænker). Når du ændrer processen, forsvinder mylderet af sig selv.
              </p>
            </div>
          </div>
        </section>

        {/* Sådan foregår det sektion */}
        <section className="py-16 border-t border-[#E8DDCE]">
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-12">Sådan foregår behandlingen</h2>
          <div className="space-y-12">
            {[
              { title: "Kortlægning af mønstre", content: "Jeg hjælper dig med at finde ud af, hvilke typer tanker der trigger dit mylder, og hvordan du plejer at reagere på dem." },
              { title: "Træning af opmærksomhed", content: "Du får øvelser i at flytte din opmærksomhed væk fra tankerne og tilbage til virkeligheden." },
              { title: "Hypnotisk ro", content: "Jeg bruger hypnose til at give dig en direkte oplevelse af mental stilhed og kropslig afslapning." },
              { title: "Nye strategier", content: "Du lærer konkrete metoder til at håndtere svære tanker i hverdagen, så de ikke længere kan kapre din dag." },
              { title: "Fastholdelse af roen", content: "Jeg sikrer, at du har redskaberne til at bevare overblikket og roen, selv i pressede perioder fremover." }
            ].map((step, i) => (
              <div key={i} className="flex gap-6">
                <div className="flex-shrink-0 w-10 h-10 bg-[#123F13] text-white rounded-full flex items-center justify-center font-bold">
                  {i + 1}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#123F13] mb-2">{step.title}</h3>
                  <p className="text-[#3B352F]/70 text-lg leading-relaxed">{step.content}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ Section */}
        <div className="pt-16 pb-0 border-t border-[#E8DDCE]">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-6">FAQ - Ofte stillede spørgsmål</h2>
            <p className="text-[#3B352F]/70">
              Her kan du finde svar på nogle af de spørgsmål, jeg ofte får omkring tankemylder og behandling.
            </p>
          </div>
          <div className="max-w-4xl mx-auto">
            <FAQAccordion items={[
              {
                question: "Kan man virkelig lære at styre sine tanker?",
                answer: "Du kan ikke styre, hvilke tanker der dukker op, men du kan i høj grad styre, hvor meget tid og opmærksomhed du giver dem. Det er her, friheden ligger."
              },
              {
                question: "Hvor hurtigt mærker jeg en effekt?",
                answer: "Mange mærker en lettelse allerede efter første session, fordi de får en forståelse for, hvorfor tankerne kører i ring, og får det første værktøj til at bryde mønsteret."
              },
              {
                question: "Er tankemylder det samme som stress?",
                answer: "Tankemylder er ofte et symptom på stress, men man kan sagtens have tankemylder uden at være klinisk stresset. Uanset årsagen er værktøjerne til at få ro de samme."
              },
              {
                question: "Hvor mange behandlinger skal der til?",
                answer: "De fleste har brug for mellem 3 og 6 sessioner for at få fuldstændig kontrol over deres tankemylder og integrere de nye vaner i hverdagen."
              },
              {
                question: "Virker det også, hvis jeg har haft det i mange år?",
                answer: "Ja, absolut. Selvom tankemylder føles som en fastgroet del af din personlighed, er det i virkeligheden bare en mental vane, og vaner kan altid ændres med de rette teknikker."
              }
            ]} />
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-[#3B352F]/70 mb-4">Vil du læse mere om mental sundhed og trivsel?</p>
            <a 
              href="https://psykiatrifonden.dk" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#8C2F12] font-bold hover:underline"
            >
              Besøg Psykiatrifondens hjemmeside <ExternalLink size={16} />
            </a>
          </div>
        </div>

        {/* Final CTA */}
        <div className="text-center pb-16 mt-20">
          <h2 className="text-3xl md:text-4xl font-serif text-[#123F13] mb-8">Klar til at få ro og kontrol igen?</h2>
          <Link to="/bestil-tid" className="inline-flex items-center gap-2 bg-[#8C2F12] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#a33a1a] transition-all shadow-lg">
            Book din første session nu <ArrowRight size={20} />
          </Link>
        </div>
      </div>
    </ServiceLayout>
  );
};

export default Tankemylder;
